import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { useCarrito } from "./ContextoCarrito";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { useAuth } from "../Login/AuthContext";
import { useFavoritos } from "./ContextoFavoritos";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const DetalleProducto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { agregarAlCarrito } = useCarrito();
  const { esFavorito, alternarFavorito } = useFavoritos();
  const { usuario } = useAuth();

  const [producto, setProducto] = useState(null);
  const [productosRelacionados, setProductosRelacionados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cantidad, setCantidad] = useState(1);
  const [descripcionAbierta, setDescripcionAbierta] = useState(true);
  const [enviosAbierto, setEnviosAbierto] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let activo = true;

    const cargarProducto = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/productos/${id}`,
          { signal: controller.signal }
        );
        if (!activo) return;
        setProducto(response.data);

        // Cargar productos relacionados
        const responseRelacionados = await axios.get(
          `${process.env.REACT_APP_API_URL}/productos`,
          { signal: controller.signal }
        );
        const relacionados = responseRelacionados.data
          .filter((p) => p.id !== id && p.categoria?.id === response.data.categoria?.id)
          .slice(0, 5);
        if (!activo) return;
        setProductosRelacionados(relacionados);
      } catch (error) {
        if (axios.isCancel(error)) return;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar el producto."
        });
        navigate("/tienda");
      } finally {
        if (activo) setCargando(false);
      }
    };

    cargarProducto();

    return () => {
      activo = false;
      controller.abort();
    };
  }, [id, navigate]);

  const handleAgregar = async () => {
  if (!usuario) {
    Swal.fire({
      icon: "info",
      title: "Inicia sesion",
      text: "Necesitas una cuenta para agregar productos al carrito.",
      confirmButtonText: "Iniciar sesion",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#111",
    }).then((result) => {
      if (result.isConfirmed) navigate("/login");
    });
    return;
  }

  if (cantidad < 1 || cantidad > producto.stock) return;

  const ok = await agregarAlCarrito(producto, cantidad);

  if (ok) {
    Swal.fire({
      icon: "success",
      title: "Agregado al carrito",
      showConfirmButton: false,
      timer: 1000
    });
  }
};

  const handleFavorito = () => {
    if (!usuario || usuario.rol !== "cliente") {
      Swal.fire({
        icon: "info",
        title: "Inicia sesión",
        text: "Necesitas una cuenta para guardar productos favoritos.",
        confirmButtonText: "Iniciar sesión",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#111",
      }).then((result) => {
        if (result.isConfirmed) navigate("/login");
      });
      return;
    }

    const agregado = alternarFavorito(producto);
    Swal.fire({
      icon: agregado ? "success" : "info",
      title: agregado ? "Guardado en favoritos" : "Eliminado de favoritos",
      showConfirmButton: false,
      timer: 900,
    });
  };

  if (cargando) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <p className="text-gray-600 text-lg">Cargando producto...</p>
    </div>
  );

  if (!producto) return null;

  const imageUrl = producto.imagen_url
    ? `${BASE_URL}${producto.imagen_url}`
    : "https://via.placeholder.com/400";

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 md:py-8">
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mb-8 md:mb-12 break-words">
          <button onClick={() => navigate("/tienda")} className="hover:text-black">
            TIENDA
          </button>
          <span>/</span>
          {producto?.categoria && (
            <>
              <span>{producto.categoria.nombre}</span>
              <span>/</span>
            </>
          )}
          <span className="text-black font-medium">{producto?.nombre}</span>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-14 md:mb-20">
          {/* Imagen Izquierda - Más compacta */}
          <div className="flex flex-col">
            <img
              src={imageUrl}
              alt={producto?.nombre}
              className="w-full max-h-[520px] object-cover rounded-lg bg-gray-100 mb-4"
            />
            {/* Miniaturas */}
            <div className="flex gap-3">
              <img
                src={imageUrl}
                alt="thumbnail 1"
                className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition"
              />
              <img
                src={imageUrl}
                alt="thumbnail 2"
                className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition"
              />
            </div>
          </div>

          {/* Detalles Derecha */}
          <div className="flex flex-col">
            {/* Título */}
            <h1 className="text-2xl md:text-3xl font-light text-black mb-4 tracking-tight break-words">
              {producto?.nombre}
            </h1>

            {/* Precio */}
            <p className="text-2xl md:text-3xl font-bold text-black mb-6">
              ${Number(producto?.precio).toLocaleString('es-AR')}
            </p>

            {/* Cantidad */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-2">
                Cantidad
              </label>
              <div className="flex items-center border border-gray-300 rounded w-fit">
                <button
                  onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                  disabled={producto?.stock === 0}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={producto?.stock}
                  value={cantidad}
                  onChange={(e) => setCantidad(Math.max(1, Number(e.target.value)))}
                  className="w-12 p-2 text-center border-l border-r border-gray-300 text-sm focus:outline-none"
                  disabled={producto?.stock === 0}
                />
                <button
                  onClick={() => setCantidad(Math.min(producto?.stock, cantidad + 1))}
                  disabled={producto?.stock === 0}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  +
                </button>
              </div>
            </div>

            {/* Botón Agregar + Favorito */}
            <div className="flex flex-col gap-3 mb-8 sm:flex-row sm:items-center">
              <button
                onClick={handleAgregar}
                disabled={producto?.stock === 0}
                className="w-full bg-black text-white px-6 py-3 rounded font-semibold hover:bg-gray-900 transition disabled:opacity-50 disabled:cursor-not-allowed tracking-wide uppercase text-sm sm:flex-1"
              >
                {producto?.stock === 0 ? "Agotado" : "Agregar al carrito"}
              </button>

              <button
                onClick={handleFavorito}
                className="flex w-full items-center justify-center rounded border border-gray-300 p-3 transition hover:border-rose-200 hover:bg-rose-50 sm:w-auto"
                title={esFavorito(producto.id) ? "Eliminar de favoritos" : "Agregar a favoritos"}
                aria-label={esFavorito(producto.id) ? "Eliminar de favoritos" : "Agregar a favoritos"}
                aria-pressed={esFavorito(producto.id)}
              >
                {esFavorito(producto.id) ? (
                  <FaHeart className="text-lg text-rose-600" />
                ) : (
                  <FaRegHeart className="text-gray-600 text-lg" />
                )}
              </button>
            </div>

            {/* Stock */}
            {producto?.stock > 0 && (
              <p className="text-xs text-gray-600 mb-6">
                {producto.stock} disponible{producto.stock !== 1 ? "s" : ""}
              </p>
            )}

            {/* Descripción */}
            <div className="border-t border-gray-200 py-4">
              <button
                onClick={() => setDescripcionAbierta(!descripcionAbierta)}
                className="flex justify-between items-center w-full py-2 text-xs font-semibold text-black uppercase tracking-wide hover:text-gray-700"
              >
                <span>Descripción</span>
                <span className="text-lg">{descripcionAbierta ? "−" : "+"}</span>
              </button>
              {descripcionAbierta && (
                <p className="text-gray-700 text-sm leading-relaxed mt-4">
                  {producto?.descripcion}
                </p>
              )}
            </div>

            {/* Envíos */}
            <div className="border-t border-b border-gray-200 py-4">
              <button
                onClick={() => setEnviosAbierto(!enviosAbierto)}
                className="flex justify-between items-center w-full py-2 text-xs font-semibold text-black uppercase tracking-wide hover:text-gray-700"
              >
                <span>Envíos</span>
                <span className="text-lg">{enviosAbierto ? "−" : "+"}</span>
              </button>
              {enviosAbierto && (
                <p className="text-gray-700 text-sm leading-relaxed mt-4">
                  Realizamos envíos a todo el país. Consulta costos y tiempos.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Productos Relacionados */}
        {productosRelacionados.length > 0 && (
          <div className="mt-14 md:mt-20 pt-10 md:pt-12 border-t border-gray-200">
            <h2 className="text-center text-sm font-semibold text-black uppercase tracking-widest mb-12">
              Productos que te pueden interesar
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {productosRelacionados.map((prod) => {
                const prodImageUrl = prod.imagen_url
                  ? `${BASE_URL}${prod.imagen_url}`
                  : "https://via.placeholder.com/400x500?text=Sin+imagen";
                return (
                  <div
                    key={prod.id}
                    onClick={() => navigate(`/producto/${prod.id}`)}
                    className="flex flex-col bg-white rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition"
                  >
                    <div className="w-full h-64 bg-gray-100 overflow-hidden rounded-lg mb-4">
                      <img
                        src={prodImageUrl}
                        alt={prod.nombre}
                        className="w-full h-full object-cover hover:scale-105 transition duration-300"
                      />
                    </div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 break-words">
                      {prod.categoria?.nombre}
                    </p>
                    <h3 className="text-sm font-semibold text-black mb-2 line-clamp-2 h-10">
                      {prod.nombre}
                    </h3>
                    <p className="text-lg font-bold text-black">
                      ${Number(prod.precio).toLocaleString('es-AR')}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleProducto;
